/**
 * D85 — the product elevation stack.
 *
 * D12 gives the system two devices: a hairline border, or one surface step
 * (900 → 800 → 700). D74 then extends the shadow allowance to persistent chrome
 * that floats above scrolling content, not only transient overlays — but keeps
 * P2 intact with a condition rather than a ban:
 *
 *   **A floating surface must ALSO be drawn.** It carries a hairline or a
 *   surface step, and the shadow is additive on top — never the only thing
 *   separating it from the page. A shadow doing the structural work alone is a
 *   defect; a shadow reinforcing a drawn edge is not.
 *
 * Three steps is enough for a marketing page. A product stacks page → panel →
 * card → popover → menu → modal → drawer → tooltip, which the design system
 * names as a known gap. This extends the ladder; it does not replace D12.
 *
 * The rule that keeps it honest: **every level below is DRAWN.** Only the two
 * that genuinely float add a shadow, and both are drawn as well.
 */

/** The one overlay shadow the system defines (`--elev-overlay`). */
const OVERLAY = "0 16px 48px rgba(0,0,0,0.6)";

export const elevation = {
  /**
   * The page. Nothing above it, nothing under it.
   */
  page: { surface: "default", drawn: "none", shadow: "none" },

  /**
   * A panel or a card. DRAWN with a hairline — no shadow, because it is not
   * floating above anything, it is part of the page.
   */
  panel: { surface: "default", drawn: "hairline", shadow: "none" },

  /**
   * A raised region inside a panel — a table head, a well. One surface step,
   * no shadow.
   */
  raised: { surface: "raised", drawn: "hairline", shadow: "none" },

  /**
   * Persistent chrome — a fixed nav or sidebar. D74's case: it floats above
   * scrolling content, so it may take a shadow, but ONLY because it is also
   * drawn. The system verified this shape against its own v1 nav.
   */
  chrome: {
    surface: "raised",
    drawn: "hairline",
    shadow: "0 1px 2px rgba(0,0,0,0.34)",
  },

  /**
   * Transient overlays — popover, menu, dialog, drawer, toast. Drawn AND
   * shadowed: these genuinely float, and losing track of which surface you are
   * on is the failure the shadow prevents.
   */
  overlay: OVERLAY,
} as const;

/**
 * D88 — TRANSLUCENCY IS RETIRED, and this note is the record of why.
 *
 * The platform had a `glass` token set: `backdrop-filter: saturate(180%)
 * blur(20px)` on the top bar and command palette, with a documented fallback
 * for browsers without support and an explicit ban on using it for full-screen
 * modals. It came from the Apple lineage, where layering is done by
 * translucency and blur rather than by drawing.
 *
 * Aerios has no equivalent, and P2 is the opposite instinct: structure is
 * drawn — hairlines, alignment, space — with shadows and blurs barred as
 * PRIMARY devices. D74 already gives chrome everything it needs: a surface
 * step, a hairline, and an additive shadow, which is exactly the treatment the
 * system validated on its own floating nav.
 *
 * So this removes an invented value rather than adding one. If translucency is
 * wanted back, it needs a D-number from the system side, not a token here.
 */
export const GLASS_RETIRED_SEE_D88 = true as const;
